import React, {useCallback, useEffect, useMemo, useState} from "react";
import Api from "../../../../../../core/services/api/api";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {formatMoney} from "../../../../../../core/services/utils/utils";
import AvailableCreditsSearchSection from "../../../../../components/search-sections/available-credits";
import AvailableCreditsTable from "../../../../../components/app-specific/available-credits-table";
import {AvailableCreditsDataGridColumnNames} from "../../../../../../core/constants/table/data-grid-column-keys";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import DateUtils from "../../../../../../core/services/date-utils";
import {useParams} from "react-router-dom";
import {useAppSelector} from "../../../../../redux";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import useAccessLevels from "../../../../../hooks/use-access-levels";
import {Typography} from "@mui/material";


const dataGridSaveConfig = {
    name: 'CustomerAvailableCreditsViewDataGrid',
    version: 1,
}

const CustomerAvailableCreditsView = () => {
    const params = useParams();
    const [loading, setLoading] = useState(true);
    const [credits, setCredits] = useState([]);
    const [filters, setFilters] = useState({});
    const isMounted = useIsMounted();

    const defaultCustomer = useAppSelector(state => state.customerInformation);
    const {configureNotebookTags} = useConfigureNotebook('customer-available-credits-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorAccessLevels = useAccessLevels();
    const canExportCustomerAvailableCredits = operatorAccessLevels.packages.sales.exportCustomerAvailableCredits;

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral tool for the view
     */
    useEffect(() => {
        if (!defaultCustomer?.code)
            return;
        configureNotebookTags((tags) => tags.customerAccountingAvailableCredits(defaultCustomer?.code));
        configureBehavioralTool((tags) => tags.customerAccountingAvailableCredits(defaultCustomer?.code));
    }, [configureBehavioralTool, configureNotebookTags, defaultCustomer?.code]);

    const visibleCredits = useMemo(() => {
        let results = Array.from(credits);
        if (filters?.keyword?.length) {
            results = results?.filter(e =>
                e.transactionNo?.toLowerCase().includes(filters.keyword.trim().toLowerCase())
                || e.transactionTypeName?.toLowerCase().includes(filters.keyword.trim().toLowerCase())
                || e.referenceDisplay?.toLowerCase().includes(filters.keyword.trim().toLowerCase())
            )
        }
        if (filters?.toDate) {
            results = results?.filter(e => DateUtils.server(e.submittedDate).isSameOrBefore(DateUtils.server(filters?.toDate)))
        }
        if (filters?.fromDate) {
            results = results?.filter(e => DateUtils.server(e.submittedDate).isSameOrAfter(DateUtils.server(filters?.fromDate)))
        }
        if (filters?.minAmount) {
            results = results?.filter(e => -e.balance >= Number(filters.minAmount));
        }
        if (filters?.maxAmount) {
            results = results?.filter(e => -e.balance <= Number(filters.maxAmount));
        }
        return results;
    }, [credits, filters])

    /**
     * Fetches the available credits of the selected user form the server.
     */
    const getAvailableCredits = useCallback(async () => {
        const response = await Api.getAllAvailableCreditByCustomer(params.customerId)
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            const result = response?.data ?? [];
            setCredits(result);
        }
    }, [params.customerId, isMounted])

    /**
     * With each change in the customerId value of the url params:
     * - fetches the available credits of the selected user form the server.
     */
    useEffect(() => {
        getAvailableCredits().then();
    }, [getAvailableCredits])


    /**
     * Formats the exported data's entries and returns the info to be exported.
     * @return {DataGridExportInfo}
     */
    const formatExportingData = async () => {
        return {
            title: `Available Credits`,
            body: credits?.map(e => ({
                'Transaction Number': e.transactionNo ?? '',
                'Submit Date': e.submittedDate ? DateUtils.serverToLocal(e.submittedDate).format(DateUtils.dateFormat) : '',
                'Type': e.transactionTypeName ?? '',
                'Credit': formatMoney(e.amount),
                'Balance': formatMoney(e.balance),
                'Reference Number': e.referenceDisplay ?? '',
            })) ?? [],
        }
    }

    return (
        <>
            <div className={'d-flex flex-column'}>
                <Typography variant={'h6'} fontWeight={600} marginInlineStart={1}>
                    {defaultCustomer?.name ?? ""}'s Available Credits
                </Typography>
                <div className='p-3'>
                    <AvailableCreditsSearchSection filters={filters} setFilters={setFilters}/>
                    <div className='my-5'/>
                    <AvailableCreditsTable
                        credits={visibleCredits}
                        loading={loading}
                        selectedCreditTransactionNos={[]}
                        hideExporting={false}
                        getExportInfo={canExportCustomerAvailableCredits ? formatExportingData : undefined}
                        excludedColumns={[AvailableCreditsDataGridColumnNames.select,]}
                        saveConfig={dataGridSaveConfig}
                    />
                </div>
            </div>
        </>
    );
}

export default CustomerAvailableCreditsView;
